"use client";

import React, { useEffect, useRef, useState } from "react";
import { MdOutlineClose, MdWarning } from "react-icons/md";

const DemoBadge: React.FC = () => {
  const [isDemo, setIsDemo] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(true);
  const badgeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsDemo(window.location.hostname.split(".")[0] === "demo");
  }, []);

  useEffect(() => {
    if (isDemo && badgeRef.current) {
      const windowWidth = window.innerWidth;
      const badgeWidth = badgeRef.current.offsetWidth;
      setPosition({ x: (windowWidth - badgeWidth) / 2, y: 40 });
    }
  }, [isDemo]);

  useEffect(() => {
    const badge = badgeRef.current;
    if (!badge) return;

    let isDragging = false;
    let startX: number, startY: number;

    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault(); // Prevent text selection
      isDragging = true;
      startX = e.clientX - parseFloat(badge.style.left || "0");
      startY = e.clientY - parseFloat(badge.style.top || "0");
      badge.style.cursor = "grabbing";
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault(); // Prevent text selection
      const newX = e.clientX - startX;
      const newY = e.clientY - startY;
      badge.style.left = `${newX}px`;
      badge.style.top = `${newY}px`;
    };

    const onMouseUp = () => {
      isDragging = false;
      badge.style.cursor = "grab";
      // Update the position state after dragging ends
      const rect = badge.getBoundingClientRect();
      setPosition({ x: rect.left, y: rect.top });
    };

    badge.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      badge.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  if (!isDemo || !isVisible) return null;

  return (
    <div
      ref={badgeRef}
      className="fixed z-50 max-w-xs cursor-grab select-none rounded-lg border-2 border-primary/20 bg-primary/10 p-3 text-primary shadow-lg transition-all duration-300 ease-in-out"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      <button
        onClick={() => setIsVisible(false)}
        className="absolute right-2 top-1 rounded-md text-primary"
      >
        <MdOutlineClose className="h-4 w-4" />
      </button>
      <p className="flex items-center justify-center text-center text-body-emphasis">
        <MdWarning className="mr-2 h-5 w-5 text-yellow-500" />
        Demo Environment
      </p>
      <p className="mt-1 text-center text-caption text-foreground-secondary">
        This is a demo environment. All information here is for demonstration
        purposes only and does not contain real patient data.
      </p>
    </div>
  );
};

export default DemoBadge;
